<template>
  <div class="el-cond">
    <label class="cp-label">{{ L.showWhen || 'Show only when…' }}</label>
    <div class="el-cond__row">
      <CustomSelect :value="triggerId" :options="triggerOptions" @input="setTrigger($event)" />
    </div>

    <!-- Choice trigger → pick one or MORE options; element shows when ANY is selected. -->
    <div v-if="showOptionPicker" class="el-cond__opts">
      <span class="el-cond__opts-label">{{ L.showWhenAnyOf || 'when any of these is selected:' }}</span>
      <label v-for="opt in optionOptions" :key="opt.value" class="el-cond__opt">
        <span>{{ opt.label }}</span>
        <CustomSwitch :value="isOptionOn(opt.value)" @input="toggleOption(opt.value, $event)" />
      </label>
    </div>

    <p class="cp-hint cp-hint--sm">
      {{ L.showWhenHint || 'This element stays hidden until the chosen answer is selected, then it appears (and becomes required).' }}
    </p>
  </div>
</template>

<script>
import CustomSelect from '../../../parts/CustomSelect.vue'
import CustomSwitch from '../../../parts/CustomSwitch.vue'

/**
 * "Show only when…" condition picker for an element. The trigger can be any sibling
 * single-checkbox / single / multiple element in the same box. For a checkbox trigger
 * the condition fires when it's ON; for a choice trigger, when ANY of the selected
 * option values is picked. Mutates element.condition in place
 * ({ elementId, optionValues:[...] } | null).
 */
export default {
  name: 'ElementCondition',
  components: { CustomSelect, CustomSwitch },
  props: {
    options: { type: Object, required: true },
    element: { type: Object, required: true },
    // All elements in the box (to offer triggers); the current element is excluded.
    boxElements: { type: Array, default: () => [] },
    lang: { type: String, default: 'de' }
  },
  computed: {
    L() { return this.options.Lang || {}; },
    // Eligible triggers: checkbox / single / multiple, not this element itself.
    triggers() {
      return (this.boxElements || []).filter(e =>
        e !== this.element && ['checkbox', 'single', 'multiple'].includes(e.type));
    },
    triggerOptions() {
      const L = this.L;
      const none = { value: '', label: L.showAlways || 'Always show' };
      return [none].concat(this.triggers.map(e => ({
        value: e.id,
        label: this.triggerLabel(e)
      })));
    },
    triggerId() { return (this.element.condition && this.element.condition.elementId) || ''; },
    triggerEl() { return this.triggers.find(e => e.id === this.triggerId) || null; },
    showOptionPicker() {
      return this.triggerEl && (this.triggerEl.type === 'single' || this.triggerEl.type === 'multiple');
    },
    optionValues() {
      const c = this.element.condition;
      if (!c) return [];
      if (Array.isArray(c.optionValues)) return c.optionValues;
      return (c.optionValue != null && c.optionValue !== '') ? [c.optionValue] : [];
    },
    optionOptions() {
      if (!this.triggerEl) return [];
      return (this.triggerEl.options || []).map(o => ({
        value: o.value,
        label: (o.labels && (o.labels[this.lang] || o.labels.en)) || o.value || '(option)'
      }));
    }
  },
  methods: {
    triggerLabel(e) {
      const L = this.L;
      const txt = (e.labels && (e.labels[this.lang] || e.labels.en)) || '';
      const kind = e.type === 'checkbox' ? (L.elType_checkbox || 'Single checkbox')
        : e.type === 'single' ? (L.elType_single || 'Single choice')
        : (L.elType_multiple || 'Multiple choice');
      return txt ? `${txt} (${kind})` : kind;
    },
    setTrigger(id) {
      if (!id) { this.$set(this.element, 'condition', null); return; }
      const t = this.triggers.find(e => e.id === id);
      // Checkbox trigger → no options (fires when ON). Choice → default to the first option.
      const optionValues = (t && (t.type === 'single' || t.type === 'multiple') && t.options && t.options[0])
        ? [t.options[0].value] : [];
      this.$set(this.element, 'condition', { elementId: id, optionValues });
    },
    isOptionOn(val) { return this.optionValues.map(String).includes(String(val)); },
    toggleOption(val, on) {
      if (!this.element.condition) return;
      const cur = this.optionValues.slice();
      const i = cur.findIndex(v => String(v) === String(val));
      if (on && i === -1) cur.push(val);
      else if (!on && i !== -1) cur.splice(i, 1);
      this.$set(this.element.condition, 'optionValues', cur);
    }
  }
};
</script>

<style scoped>
.el-cond { margin-top: 0.75rem; padding-top: 0.6rem; border-top: 1px dashed #e6e6e6; }
.el-cond__row { display: flex; gap: 0.6rem; flex-wrap: wrap; }
.el-cond__row > * { min-width: 180px; flex: 1; }
.el-cond__opts { margin-top: 0.6rem; display: flex; flex-direction: column; gap: 0.4rem; }
.el-cond__opts-label { font-size: 0.74rem; color: #888; }
.el-cond__opt { display: flex; align-items: center; justify-content: space-between; gap: 0.6rem; font-size: 0.85rem; color: #444; max-width: 360px; }
.el-cond__opt > span { flex: 1; min-width: 0; }
.cp-label { display: block; font-size: 0.78rem; font-weight: 600; color: #555; margin-bottom: 0.35rem; }
.cp-hint { font-size: 0.8rem; color: #888; margin: 0.4rem 0 0; }
.cp-hint--sm { font-size: 0.72rem; }
</style>
